import { useQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import type { DisciplinarySettings } from "../types";

export interface SuspensionViolation {
    playerId: string;
    matchId: string;
    matchDate: string;
    matchLabel: string;
    matchLabelAr: string | null;
    playerName: string | null;
    playerNameAr: string | null;
    licenseNumber: string | null;
    isStarter: boolean;
    teamName: string | null;
    teamNameAr: string | null;
    teamId: string | null;
    remainingSuspension: number;
}

function mapViolation(v: any): SuspensionViolation {
    return {
        playerId: v.player_id ?? v.playerId,
        matchId: v.match_id ?? v.matchId,
        matchDate: v.match_date ?? v.matchDate,
        matchLabel: v.match_label ?? v.matchLabel,
        matchLabelAr: v.match_label_ar ?? v.matchLabelAr ?? null,
        playerName: v.player_name ?? v.playerName,
        playerNameAr: v.player_name_ar ?? v.playerNameAr ?? null,
        licenseNumber: v.license_number ?? v.licenseNumber,
        isStarter: v.is_starter ?? v.isStarter,
        teamName: v.team_name ?? v.teamName,
        teamNameAr: v.team_name_ar ?? v.teamNameAr ?? null,
        teamId: v.team_id ?? v.teamId ?? null,
        remainingSuspension: v.remaining_suspension ?? v.remainingSuspension,
    };
}

/** Fire-and-forget: write violations result to cache */
function writeViolationsCache(leagueId: string, season: string, violations: SuspensionViolation[]) {
    supabase.rpc("upsert_suspension_violations_cache", {
        p_league_id: leagueId,
        p_season: season,
        p_total_count: violations.length,
        p_violations: violations,
    }).then(({ error }) => {
        if (error) console.warn("[violations-cache] write failed:", error.message);
    });
}

/** Racine des clés liste + badge ; invalidée par le temps réel à chaque changement de `sanctions`. */
export const SUSPENSION_VIOLATIONS_QUERY_KEY_PREFIX = "suspensionViolations";

export function useSuspensionViolations(
    leagueId: string | null | undefined,
    season: string | null | undefined,
    settings: DisciplinarySettings | null | undefined
) {
    return useQuery({
        queryKey: [SUSPENSION_VIOLATIONS_QUERY_KEY_PREFIX, leagueId, season, settings],
        queryFn: async (): Promise<SuspensionViolation[]> => {
            if (!leagueId || !season) return [];

            // 1. Try cache first (~50ms)
            const { data: cached } = await supabase
                .from("suspension_violations_cache")
                .select("violations, stale")
                .eq("league_id", leagueId)
                .eq("season", season)
                .maybeSingle();

            if (cached && !cached.stale) {
                return (Array.isArray(cached.violations) ? cached.violations : []).map(mapViolation);
            }

            // 2. Cache miss or stale — call edge function
            const { data, error } = await supabase.functions.invoke(
                "get-suspension-violations",
                { body: { leagueId, season, settings } }
            );

            if (error) {
                // If EF fails but we have stale cache, return stale data
                if (cached) {
                    return (Array.isArray(cached.violations) ? cached.violations : []).map(mapViolation);
                }
                const detail = (data as any)?.error ?? error.message;
                throw new Error(`get-suspension-violations: ${detail}`);
            }

            const violations = (data?.violations ?? []).map(mapViolation);

            // 3. Write to cache (fire-and-forget)
            writeViolationsCache(leagueId, season, violations);

            return violations;
        },
        enabled: !!leagueId && !!season,
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Badge de l'onglet Suspensions : violations encore à traiter, c.-à-d. la même
 * liste que le kanban (cache frais, sinon recalcul via
 * `get-suspension-violations` écrit dans le cache) moins celles dont la
 * résolution est « resolved ». Lire `total_count` seul ignorait `stale` et les
 * résolutions : le badge restait sur un compte périmé et ne descendait jamais.
 * Never throws — the badge is "soft" info and falls back to the stale cache,
 * then 0, on any edge-function failure.
 */
export function useSuspensionViolationsCount(
    leagueId: string | null | undefined,
    season: string | null | undefined
) {
    return useQuery({
        queryKey: [SUSPENSION_VIOLATIONS_QUERY_KEY_PREFIX, "count", leagueId, season],
        queryFn: async (): Promise<number> => {
            if (!leagueId || !season) return 0;
            const { data: cached } = await supabase
                .from("suspension_violations_cache")
                .select("violations, stale")
                .eq("league_id", leagueId)
                .eq("season", season)
                .maybeSingle();

            const cachedViolations = (): SuspensionViolation[] =>
                (Array.isArray(cached?.violations) ? cached.violations : []).map(mapViolation);

            let violations: SuspensionViolation[];
            if (cached && !cached.stale) {
                violations = cachedViolations();
            } else {
                try {
                    const { data: computed, error } = await supabase.functions.invoke(
                        "get-suspension-violations",
                        { body: { leagueId, season } }
                    );
                    if (error) {
                        violations = cachedViolations();
                    } else {
                        violations = (Array.isArray(computed?.violations) ? computed.violations : []).map(mapViolation);
                        writeViolationsCache(leagueId, season, violations);
                    }
                } catch {
                    violations = cachedViolations(); // badge stays "soft" — never throw for a badge
                }
            }
            if (violations.length === 0) return 0;

            const { data: resolved } = await supabase
                .from("suspension_violation_resolutions")
                .select("player_id, match_id")
                .eq("league_id", leagueId)
                .eq("season", season)
                .eq("status", "resolved");
            const resolvedKeys = new Set((resolved ?? []).map((r) => `${r.player_id}:${r.match_id}`));
            return violations.filter((v) => !resolvedKeys.has(`${v.playerId}:${v.matchId}`)).length;
        },
        enabled: !!leagueId && !!season,
        staleTime: 60 * 1000,
    });
}
